import { gql } from '@apollo/client';
import { workPackageFragments } from '../work-packages';

// Subscription: planningAiCompleted
export const SUBSCRIPTION_PLANNING_AI_COMPLETED = gql`
  subscription onPlanningAiCompleted {
    planningAiCompleted {
      planningAiRequestId
      foremanId
      status
      count
      statusMessageData
      statusMessage
    }
  }
`;

// Subscription: planningWeekSaved (manual drag-drop saves)
export const SUBSCRIPTION_PLANNING_WEEK_SAVED = gql`
  subscription onPlanningWeekSaved {
    planningWeekSaved {
      foremanId
      milestoneCount
    }
  }
`;

// Subscription: milestonesUnplanned
export const SUBSCRIPTION_MILESTONES_UNPLANNED = gql`
  subscription onMilestonesUnplanned {
    milestonesUnplanned {
      foremanId
      milestoneCount
    }
  }
`;

export const planningFragments = {
  milestoneStats: gql`
    fragment MilestoneStats on MilestoneStats {
      id
      numMilestones
      quantity {
        ...Quantity
      }
      hours
    }
    ${workPackageFragments.quantity}
  `,
};
export const EXPIRED_MILESTONES = gql`
  query expiredMilestones($input: PlanningCrewMemberInput) {
    expiredMilestones(input: $input) {
      id
      milestones {
        id
        name
        hours
        isExpired
        plannedStartDate
        currentStatus {
          id
          codeI18n
        }
        commodity {
          materialCommodity {
            id
            tag
            type {
              id
              name
            }
            workPackage {
              tag
            }
          }
          weld {
            id
            tag
            type {
              id
              name
            }
            workPackage {
              tag
            }
          }
        }
      }
    }
  }
`;
export const EXPIRED_MILESTONES_COUNT = gql`
  query expiredMilestoneCount($input: PlanningCrewMemberInput) {
    expiredMilestoneCount(input: $input) {
      id
      numExpiredMilestones
    }
  }
`;

const fragments = {
  planningWeeks: gql`
    fragment PlanningWeeks on PlanningWeekStats {
      id
      weekNumber
      year
      startDate
      endDate
    }
  `,
};

export const GET_PLANNING_WEEKS = gql`
  query planningWeekStats($input: PlanningFilterInput) {
    planningWeekStats(input: $input) {
      ...PlanningWeeks
    }
  }
  ${fragments.planningWeeks}
`;

export const GET_PLANNING_WEEK_DATA = gql`
  query planningWeekStats($input: PlanningFilterInput) {
    planningWeekStats(input: $input) {
      ...PlanningWeeks
      committedMilestoneStats {
        id
        numMilestones
        quantity {
          ...Quantity
        }
        hours
      }
      claimedMilestoneStats {
        id
        numMilestones
        quantity {
          ...Quantity
        }
        hours
      }
      plannedMilestoneStats {
        id
        numMilestones
        quantity {
          ...Quantity
        }
        hours
      }
      target {
        id
        hours
      }
      crewMember {
        id
        firstName
        lastName
        persona
      }
    }
  }
  ${workPackageFragments.quantity}
  ${fragments.planningWeeks}
`;
export const GET_PLANNED_MILESTONES = gql`
  query plannedMilestones($input: PlanningFilterInput) {
    plannedMilestones(input: $input) @connection(key: "plannedMilestones", filter: ["input"]) {
      id
      weekNumber
      year
      startDate
      endDate
      milestones(input: $input) {
        id
        name
        lobMilestoneTypeId
        projectMilestoneTypeId
        isExpired
        hours
        plannedStartDate
        planningAiRequestId
        commodity {
          weld {
            id
            tag
            workPackage {
              id
              plannedMilestoneStartDate
              plannedMilestoneEndDate
              tag
              startDate
              endDate
              facilities {
                id
              }
              primaryOwners {
                crewMember {
                  id
                }
              }
              secondaryOwners {
                crewMember {
                  id
                }
              }
              otherOwners {
                crewMember {
                  id
                }
              }
            }
            type {
              id
              name
            }
          }
          materialCommodity {
            id
            tag
            workPackage {
              id
              plannedMilestoneStartDate
              plannedMilestoneEndDate
              tag
              startDate
              endDate
              facilities {
                id
              }
              primaryOwners {
                crewMember {
                  id
                }
              }
              secondaryOwners {
                crewMember {
                  id
                }
              }
              otherOwners {
                crewMember {
                  id
                }
              }
            }
            type {
              id
              name
            }
            material {
              mwrs {
                mwrNumber
                mwrDate
                mwrStatus
              }
              mwrNumber
              mrrNumber
              mrrDate
              deliveryDate
            }
          }
        }
        currentStatus {
          id
          codeI18n
          createdAt
        }
        quantity {
          ...Quantity
        }
      }
    }
    ${workPackageFragments.quantity}
  }
`;

export const GET_PLANNING_BACKLOG_MILESTONE_LIST = gql`
  query backlogMilestones($input: PlanningBacklogMilestonesInput) {
    backlogMilestones(input: $input) @connection(key: "backlogMilestones", filter: ["input"]) {
      id
      milestones {
        id
        currentStatus {
          id
          codeI18n
          createdAt
        }
        hours
        lobMilestoneTypeId
        projectMilestoneTypeId
        name
        plannedStartDate
        isExpired
        commodity {
          weld {
            id
            tag
            workPackage {
              id
              plannedMilestoneStartDate
              plannedMilestoneEndDate
              tag
              startDate
              endDate
              facilities {
                id
              }
              primaryOwners {
                crewMember {
                  id
                }
              }
              secondaryOwners {
                crewMember {
                  id
                }
              }
              otherOwners {
                crewMember {
                  id
                }
              }
            }
            type {
              id
              name
            }
          }
          materialCommodity {
            id
            tag
            workPackage {
              id
              plannedMilestoneStartDate
              plannedMilestoneEndDate
              tag
              startDate
              endDate
              facilities {
                id
              }
              primaryOwners {
                crewMember {
                  id
                }
              }
              secondaryOwners {
                crewMember {
                  id
                }
              }
              otherOwners {
                crewMember {
                  id
                }
              }
            }
            type {
              id
              name
            }
            material {
              mwrs {
                mwrNumber
                mwrDate
                mwrStatus
              }
              mwrNumber
              mrrNumber
              mrrDate
              deliveryDate
            }
          }
        }
        quantity {
          ...Quantity
        }
      }
    }
    ${workPackageFragments.quantity}
  }
`;

export const SAVE_PLAN = gql`
  mutation($input: SavePlanningInput!) {
    savePlanning(input: $input) {
      commodityMilestones {
        id
        weekNumber
        plannedEndDate
        plannedStartDate
        isExpired
        planningAiRequestId
        commodity {
          materialCommodity {
            id
            workPackage {
              id
              plannedMilestoneStartDate
              plannedMilestoneEndDate
            }
          }
          weld {
            id
            workPackage {
              id
              plannedMilestoneStartDate
              plannedMilestoneEndDate
            }
          }
        }
        currentStatus {
          id
          codeI18n
          createdAt
        }
      }
      error {
        errorCode
        commodityMilestones {
          id
          name
          weekNumber
          plannedEndDate
          plannedStartDate
          planningAiRequestId
          currentStatus {
            id
            codeI18n
            createdAt
          }
          commodity {
            weld {
              id
              tag
              type {
                id
                name
              }
            }
            materialCommodity {
              id
              tag
              type {
                id
                name
              }
            }
          }
        }
      }
      planningStats {
        planningWeekStats {
          id
          weekNumber
          year
          startDate
          endDate
          committedMilestoneStats {
            id
            numMilestones
            quantity {
              ...Quantity
            }
            hours
          }
          claimedMilestoneStats {
            id
            numMilestones
            quantity {
              ...Quantity
            }
            hours
          }
          plannedMilestoneStats {
            id
            numMilestones
            quantity {
              ...Quantity
            }
            hours
          }
          target {
            id
            hours
          }
          crewMember {
            id
            firstName
            lastName
            persona
          }
        }
      }
      expiredMilestoneCount {
        id
        numExpiredMilestones
      }
    }
    ${workPackageFragments.quantity}
  }
`;

export const UNPLAN = gql`
  mutation($input: UnplanInput!) {
    unplan(input: $input) {
      commodityMilestones {
        id
        weekNumber
        plannedEndDate
        plannedStartDate
        isExpired
        hours
        name
        lobMilestoneTypeId
        projectMilestoneTypeId
        planningAiRequestId
        currentStatus {
          id
          codeI18n
          createdAt
        }
        commodity {
          weld {
            id
            tag
            type {
              id
              name
            }
            workPackage {
              id
              plannedMilestoneStartDate
              plannedMilestoneEndDate
              tag
              startDate
              endDate
              primaryOwners {
                crewMember {
                  id
                }
              }
              secondaryOwners {
                crewMember {
                  id
                }
              }
              otherOwners {
                crewMember {
                  id
                }
              }
            }
          }
          materialCommodity {
            id
            tag
            type {
              id
              name
            }
            workPackage {
              id
              plannedMilestoneStartDate
              plannedMilestoneEndDate
              tag
              startDate
              endDate
              primaryOwners {
                crewMember {
                  id
                }
              }
              secondaryOwners {
                crewMember {
                  id
                }
              }
              otherOwners {
                crewMember {
                  id
                }
              }
            }
            material {
              mwrs {
                mwrNumber
                mwrDate
                mwrStatus
              }
              mwrNumber
              mrrNumber
              mrrDate
              deliveryDate
            }
          }
        }
        quantity {
          ...Quantity
        }
      }
      error {
        errorCode
        commodityMilestones {
          id
          weekNumber
          plannedEndDate
          plannedStartDate
          planningAiRequestId
          currentStatus {
            codeI18n
          }
        }
      }
      planningStats {
        planningWeekStats {
          id
          weekNumber
          year
          startDate
          endDate
          committedMilestoneStats {
            id
            numMilestones
            quantity {
              ...Quantity
            }
            hours
          }
          claimedMilestoneStats {
            id
            numMilestones
            quantity {
              ...Quantity
            }
            hours
          }
          plannedMilestoneStats {
            id
            numMilestones
            quantity {
              ...Quantity
            }
            hours
          }
          target {
            id
            hours
          }
          crewMember {
            id
            firstName
            lastName
            persona
          }
        }
      }
      expiredMilestoneCount {
        id
        numExpiredMilestones
      }
    }
    ${workPackageFragments.quantity}
  }
`;

export const GET_WEEKLY_MILESTONE_PROGRESS = gql`
  query weeklyMilestoneProgress($input: WeeklyMilestoneProgressInput!) {
    weeklyMilestoneProgress(input: $input) {
      weeklyMilestoneAnalysis {
        targetHours
        cumulativeMilestoneHours
        cumulativeMilestoneQuantity
        targetQuantity {
          ...Quantity
        }
      }
      milestoneProgress {
        id
        date
        weekDay
        targetHours
        targetQuantity
        milestonehours
        milestoneQuantity
        milestoneApprovedHours
        milestoneApprovedQuantity
        cumulativeMilestoneHours
        cumulativeMilestoneQuantity
        milestoneClaimedInPlanHours
        milestoneClaimedInPlanQuantity
      }
    }
    ${workPackageFragments.quantity}
  }
`;

export const GET_THREE_WEEK_LOOK_AHEAD_DATA = gql`
  query weeklyLookAheadStatsByPersona($input: CrewMemberWeeklyLookAheadStatsInput!) {
    weeklyLookAheadStatsByPersona(input: $input) {
      crewMember {
        id
        firstName
        lastName
        persona
        imageUrl
        persona
      }
      weeklyLookAheadStats {
        id
        weekNumber
        year
        startDate
        endDate
        claimedInPlanMilestoneStats{
         ...MilestoneStats

        }
        approvedMilestoneStats {
         ...MilestoneStats
        }
        claimedMilestoneStats {
          ...MilestoneStats
        }
        plannedMilestoneStats {
        ...MilestoneStats
        }
        target {
        ...MilestoneStats
        }
        earningsInPlan
        planningInTarget
      }
    }
    ${planningFragments.milestoneStats}

  }
`;

export const PLAN_WITH_AI = gql`
  mutation planWithAiPlanning($input: AiPlanningInput!) {
    planAiPlanning(input: $input) {
      planningAiRequestId
      error
    }
  }
`;

export const SAVE_AI_PLANNING = gql`
  mutation saveAiPlanning($planningAiRequestId: ID!) {
    saveAiPlanning(planningAiRequestId: $planningAiRequestId) {
      commodityMilestones {
        id
        weekNumber
        plannedEndDate
        plannedStartDate
        isExpired
        planningAiRequestId
        commodity {
          materialCommodity {
            id
            workPackage {
              id
              plannedMilestoneStartDate
              plannedMilestoneEndDate
            }
          }
          weld {
            id
            workPackage {
              id
              plannedMilestoneStartDate
              plannedMilestoneEndDate
            }
          }
        }
        currentStatus {
          id
          codeI18n
          createdAt
        }
      }
      error {
        errorCode
        commodityMilestones {
          id
          name
          weekNumber
          plannedEndDate
          plannedStartDate
          planningAiRequestId
          currentStatus {
            id
            codeI18n
            createdAt
          }
          commodity {
            weld {
              id
              tag
              type {
                id
                name
              }
            }
            materialCommodity {
              id
              tag
              type {
                id
                name
              }
            }
          }
        }
      }
      planningStats {
        planningWeekStats {
          id
          weekNumber
          year
          startDate
          endDate
          committedMilestoneStats {
            id
            numMilestones
            quantity {
              ...Quantity
            }
            hours
          }
          claimedMilestoneStats {
            id
            numMilestones
            quantity {
              ...Quantity
            }
            hours
          }
          plannedMilestoneStats {
            id
            numMilestones
            quantity {
              ...Quantity
            }
            hours
          }
          target {
            id
            hours
          }
          crewMember {
            id
            firstName
            lastName
            persona
          }
        }
      }
      expiredMilestoneCount {
        id
        numExpiredMilestones
      }
    }
      ${workPackageFragments.quantity}
  }
`;

export const CANCEL_AI_PLANNING = gql`
  mutation cancelAiPlanning($planningAiRequestId: ID!) {
    cancelAiPlanning(planningAiRequestId: $planningAiRequestId) {
      commodityMilestones {
        id
      }
      error {
        errorCode
      }
    }
  }
`;
